import React, { FC, useState } from 'react';
import { View, Text, StyleSheet, LayoutChangeEvent } from 'react-native';

import { Element, scale as initialScale, fullWidth } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';
import Loading from '@components/loading';

import { IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { getBoxShadowStyle } from '@styles/shadow';
import ModalWrapper from '@components/modal/ModalWrapper';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import svgRegister from '@styles/icons/svg/svgRegister';
import { SimpleOnPress } from '@interfaces/base';
import { INFO_POPUP_OPTION_KEY } from '@v2/components/imageHandling/components/Header/constants';

export interface ImageHandlingPopupItem extends SimpleOnPress {
  title: string;
  icon: keyof typeof svgRegister;
  color: string;
  width: number;
  height: number;
  disabled?: boolean;
}

interface ImageHandlingPopupProps {
  C: ColorFunction;
  list: ImageHandlingPopupItem[];
  loading: boolean;
  onClose: () => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  iconPosition?: { x: number; y: number };
}

const ImageHandlingPopup: FC<ImageHandlingPopupProps> = ({ C, list, loading, onClose, scale = initialScale, font = initialFont, iconPosition }) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Ultra Wide', C, scheme: 'light' });
  const [modalWidth, setModalWidth] = useState(0);

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();

  const borderRadius = scale(SCREEN_BORDER_RADIUS);

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setModalWidth(width);
  };

  const getLeftPosition = () => {
    if (!iconPosition) return undefined;

    const proposedLeft = iconPosition.x;
    const modalRightEdge = proposedLeft + modalWidth;
    const screenWidth = fullWidth;
    const padding = scale(20);

    // If modal would go off the right edge, adjust left position
    if (modalRightEdge > screenWidth - padding) {
      return screenWidth - modalWidth - padding;
    }

    // If modal would go off the left edge, adjust to minimum padding
    if (proposedLeft < padding) {
      return padding;
    }

    return proposedLeft;
  };

  if (loading) {
    return (
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            },
            zIndex: 100000,
            backgroundColor: 'black',
            opacity: 0.6,
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          },
        }}
      >
        <Loading background="rgb(0,0,0)" />
      </Element>
    );
  }

  const LINE_HEIGHT = scale(48);

  return (
    <ModalWrapper isVisible={true} onBackdropPress={onClose} C={C} backdropColor="transparent" backdropOpacity={1}>
      <View
        testID={TEST_IDS.documentsInfoPopup.root}
        onLayout={handleLayout}
        style={[
          {
            position: 'absolute',
            minWidth: scale(232),
            backgroundColor: C('Background/Light/Field on modal'),
            borderRadius,
            elevation: 6,
            zIndex: 999999,
            ...shadowStyles,
          },
          iconPosition
            ? {
                top: iconPosition.y + scale(40),
                left: getLeftPosition(),
              }
            : {
                right: scale(!isPhone ? 20 : 8),
              },
          isPhone && !iconPosition && { bottom: IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT },
          !isPhone &&
            !iconPosition && {
              top: scale(safeIsOperatorPanelInfoScreen && !isWeb && !isPhone ? 80 : 70),
            },
        ]}
      >
        {list.map((item, index) => (
          <TouchableElement
            key={item.title}
            testID={INFO_POPUP_OPTION_KEY[item.title] ? TEST_IDS.documentsInfoPopup.option(INFO_POPUP_OPTION_KEY[item.title]) : undefined}
            onPress={
              item.disabled
                ? () => {}
                : () => {
                    item.onPress();
                    onClose();
                  }
            }
          >
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: scale(15),
                    paddingRight: scale(22),
                    height: LINE_HEIGHT,
                    opacity: item.disabled ? 0.5 : 1,
                    borderTopLeftRadius: index === 0 ? borderRadius : 0,
                    borderTopRightRadius: index === 0 ? borderRadius : 0,
                    borderBottomLeftRadius: index === list.length - 1 ? borderRadius : 0,
                    borderBottomRightRadius: index === list.length - 1 ? borderRadius : 0,
                    backgroundColor: C(pressHoverColor('Background/Light/Field on modal')),
                    borderBottomColor: index !== list.length - 1 ? C('Hairline/Light/Secondary') : 'transparent',
                    borderBottomWidth: StyleSheet.hairlineWidth,
                  },
                ]}
              >
                <Text
                  style={[
                    font['Body'],
                    {
                      color: C(item.disabled ? 'Button/Light/Secondary' : item.color),
                    },
                  ]}
                >
                  {item.title}
                </Text>
                <Svg icon={item.icon} width={scale(20)} height={scale(20)} color={item.disabled ? 'Button/Light/Secondary' : item.color} />
              </View>
            )}
          </TouchableElement>
        ))}
      </View>
    </ModalWrapper>
  );
};

export default ImageHandlingPopup;
